import { buildFitScene, fitOf } from "@sts/calc";
import type { PlanRoom } from "@sts/geometry";
import { MIS_3005_D_TABLE_1 } from "@sts/rules-data";
import type { PlanObject, SurveyRoom, SurveySurface } from "../api/types.js";

/**
 * What is wrong with this survey, in one list, in the words a designer uses.
 *
 * WHY IT EXISTS. A heat loss is a sum. Every term of it can be a guess, and a
 * guess adds up the same way a measurement does — so a survey that has never
 * been touched still produces a confident-looking total. The competitors show
 * a red count on the drawing for exactly this reason, and it is the single
 * most useful thing on their screen: it turns "is this finished?" from a
 * judgement into a number.
 *
 * TWO SEVERITIES, AND THE DIFFERENCE MATTERS.
 *
 *  - **blocking** — the heat loss is WRONG, not merely unreviewed, until this
 *    is answered. A room whose type nobody chose is being heated to a
 *    temperature nobody chose. A wall counted as outside that is really a
 *    party wall overstates the loss every hour of the year. A U-value that is
 *    still the platform's placeholder is a number we made up. The count of
 *    these is what goes on the drawing, and while it is above zero the
 *    heat-loss section says so rather than presenting a figure as final.
 *  - **warning** — worth a look, and it does not change the heat loss. A
 *    radiator standing free of any wall is the example: the fabric is
 *    unaffected, but the wall, door and window checks cannot apply to it, so
 *    the emitter design is the part that is unfinished.
 *
 * Mixing the two would be worse than having neither. A count that includes
 * things that do not change the answer trains people to ignore the count.
 *
 * WHERE EACH PROBLEM COMES FROM. The room and surface problems are computed
 * from the survey the API already returns — no extra request, and no chance of
 * the list and the panels disagreeing about the same room. The
 * not-snapped-to-a-wall problems need the plan's geometry, so they are derived
 * separately (`unsnappedObjects`) and folded in by `withUnsnappedObjects`; they
 * run `@sts/calc`'s own `fitOf`, which is what tags a free-standing radiator on
 * the drawing, so the row in the list and the tag on the plan are one judgement
 * rather than two that could drift apart. They add warnings only, so the
 * blocking count is the same whether the drawing has loaded or not.
 */

export type ValidationSeverity = "blocking" | "warning";

export type ValidationKind =
  | "room-type-assumed"
  | "room-temperature-below-minimum"
  | "room-no-walls"
  | "surface-u-value-assumed"
  | "surface-other-side-assumed"
  | "object-not-snapped-to-wall";

export interface ValidationProblem {
  readonly kind: ValidationKind;
  readonly severity: ValidationSeverity;
  /** What is wrong, in one sentence, naming the thing it is wrong about. */
  readonly message: string;
  /** The room this belongs to, for grouping and for the jump. */
  readonly roomId: string | null;
  readonly roomName: string | null;
  /**
   * The scan element to select on the plan — a wall's or floor's external ref,
   * or a room's — so a click on the row highlights the thing in the drawing,
   * the 3D view and the tables at once. Null when there is nothing to select.
   */
  readonly selectId: string | null;
  /** The survey surface, when the problem is about one. */
  readonly surfaceId: string | null;
  /** The plan object, when the problem is about one. */
  readonly objectId: string | null;
}

export interface ValidationReport {
  readonly problems: readonly ValidationProblem[];
  readonly blocking: number;
  readonly warnings: number;
  /** True while a heat-loss figure from this survey cannot be trusted. */
  readonly heatLossBlocked: boolean;
}

/** One free-standing object, as the plan works it out. */
export interface UnsnappedObject {
  readonly id: string;
  /** What a designer would call it: "Radiator", "Radiator (Kitchen)". */
  readonly label: string;
  /** The room it is drawn in, when the plan can tell. */
  readonly roomExternalRef?: string | null;
}

/**
 * The objects on the plan that are not against any wall.
 *
 * The same judgement the plan's own fit tags make — `@sts/calc`'s `fitOf`,
 * note kind "free-standing" — so the row in the list and the tag on the
 * drawing can never disagree. Checked storey by storey, exactly as
 * `listFitStatus` does: a first-floor radiator measured against the ground
 * floor's walls would be "against a wall" by accident.
 */
export function unsnappedObjects(
  objects: readonly PlanObject[],
  rooms: readonly PlanRoom[],
): readonly UnsnappedObject[] {
  const byStorey = new Map<number, PlanObject[]>();
  for (const object of objects) {
    const list = byStorey.get(object.storey) ?? [];
    list.push(object);
    byStorey.set(object.storey, list);
  }
  const out: UnsnappedObject[] = [];
  for (const [storey, list] of byStorey) {
    const scene = buildFitScene(rooms.filter((room) => room.story === storey));
    for (const object of list) {
      const fit = fitOf(object, list, scene, { suggest: false });
      if (!fit) continue;
      if (!fit.notes.some((note) => note.kind === "free-standing")) continue;
      out.push({
        id: object.id,
        label: object.label ?? objectWords(object.kind),
        roomExternalRef: null,
      });
    }
  }
  return out;
}

function objectWords(kind: string): string {
  return kind === "heat-pump" ? "Heat pump" : kind.charAt(0).toUpperCase() + kind.slice(1);
}

const ROOM_TYPE_WORDS: Record<string, string> = {
  "living-room": "living room",
  "dining-room": "dining room",
  "bedsitting-room": "bedsitting room",
  bedroom: "bedroom",
  "hall-landing": "hall or landing",
  kitchen: "kitchen",
  bathroom: "bathroom",
  toilet: "toilet",
};

export function roomTypeWords(type: string): string {
  return ROOM_TYPE_WORDS[type] ?? type.replace(/-/g, " ");
}

function surfaceName(surface: SurveySurface): string {
  return surface.name ?? surface.kind;
}

/**
 * A U-value nobody has looked at.
 *
 * `age-band-default` carries two different things in this system: a row of the
 * construction library a designer chose, and the platform's placeholder from a
 * scan conversion. What separates them is the confirmation — the library pick
 * is confirmed the moment it is made — so an unconfirmed default is a number
 * this platform invented, and that is what this flags.
 */
function uValueIsAPlaceholder(surface: SurfaceLike): boolean {
  if (surface.uValue.confirmedBySurveyor) return false;
  return surface.uValue.provenance === "age-band-default";
}

type SurfaceLike = Pick<SurveySurface, "uValue">;

/**
 * Everything wrong with a survey's rooms and surfaces, worst first.
 *
 * Every BLOCKING problem is here: they are all facts about a room or a
 * surface, so this one call is the whole of the count that appears on the
 * drawing and in the heat-loss section, and the two can never disagree.
 * Problems about objects drawn on the plan are warnings, are derived from
 * geometry only the drawing holds, and are merged in by
 * `withUnsnappedObjects` where that geometry is.
 */
export function validateSurvey(rooms: readonly SurveyRoom[]): ValidationReport {
  const problems: ValidationProblem[] = [];

  for (const room of rooms) {
    // A room nobody heats is not part of the heat load, so nothing about its
    // type, its temperature or its fabric can make the heat load wrong.
    // Flagging it would be a count that goes up when a designer finishes a
    // decision, which is the opposite of what the count is for.
    if (room.unheated === true) continue;

    const at = {
      roomId: room.id,
      roomName: room.name,
      selectId: room.externalRef ?? null,
      surfaceId: null,
      objectId: null,
    };

    if (room.typeAssumed === true) {
      problems.push({
        kind: "room-type-assumed",
        severity: "blocking",
        message:
          `${room.name} has no room type. The scan could not tell, so it has ` +
          `been treated as a ${roomTypeWords(room.type)} — and the room type ` +
          `is what sets the internal temperature the heat loss is calculated ` +
          `to (MIS 3005-D:2025 Table 1).`,
        ...at,
      });
    }

    // Below the Table 1 minimum is a compliance failure, not a preference:
    // §3.4.1(a) requires "internal temperatures not less than those specified
    // in Table 1". The room panel refuses to save one, so this catches a
    // figure that arrived another way — a device sync, or an older API.
    const minimum = room.minimumDesignTempC ?? MIS_3005_D_TABLE_1.data[room.type];
    if (room.designTempC !== null && minimum !== undefined && room.designTempC < minimum) {
      problems.push({
        kind: "room-temperature-below-minimum",
        severity: "blocking",
        message:
          `${room.name} is designed to ${room.designTempC} °C, below the ` +
          `${minimum} °C MIS 3005-D Table 1 requires for a ` +
          `${roomTypeWords(room.type)}. Raise it, or mark the room unheated.`,
        ...at,
      });
    }

    if (!room.surfaces.some((s) => s.kind === "wall")) {
      problems.push({
        kind: "room-no-walls",
        severity: "blocking",
        message:
          `${room.name} has no walls, so almost none of its heat loss is being ` +
          `counted. Draw its walls on the plan, or mark the room unheated.`,
        ...at,
      });
    }

    for (const surface of room.surfaces) {
      const here = {
        roomId: room.id,
        roomName: room.name,
        selectId: surface.externalRef ?? room.externalRef ?? null,
        surfaceId: surface.id,
        objectId: null,
      };

      if (uValueIsAPlaceholder(surface)) {
        problems.push({
          kind: "surface-u-value-assumed",
          severity: "blocking",
          message:
            `${surfaceName(surface)} in ${room.name} has no U-value of its own — ` +
            `${surface.uValue.value} W/m²K is the platform's placeholder. Pick a ` +
            `construction, or type the figure you measured.`,
          ...here,
        });
      }

      if (surface.adjacencyAssumed === true) {
        problems.push({
          kind: "surface-other-side-assumed",
          severity: "blocking",
          message:
            surface.kind === "floor"
              ? `The floor of ${room.name} is being treated as solid and on the ` +
                `ground because the scan could not tell. A suspended floor uses a ` +
                `much colder temperature difference (MIS 3005-D:2025 §3.4.1(d) ` +
                `rather than §3.4.1(c)). Say which it is.`
              : `${surfaceName(surface)} in ${room.name} is being counted as an ` +
                `outside wall because nothing was scanned behind it. If it is a ` +
                `party wall or backs onto a garage, the heat loss is too high. ` +
                `Say what is on the other side.`,
          ...here,
        });
      }
    }
  }

  return report(problems);
}

/**
 * The same report with the drawing's own problems folded in.
 *
 * Only warnings are added, so `blocking` and `heatLossBlocked` cannot change:
 * a screen with no plan loaded yet reports exactly the same count as one that
 * has, and nothing about the heat-loss figure depends on whether the drawing
 * has finished loading.
 */
export function withUnsnappedObjects(
  base: ValidationReport,
  rooms: readonly SurveyRoom[],
  unsnapped: readonly UnsnappedObject[],
): ValidationReport {
  if (unsnapped.length === 0) return base;
  const roomByRef = new Map<string, SurveyRoom>();
  for (const room of rooms) {
    if (room.externalRef) roomByRef.set(room.externalRef, room);
  }
  const extra = unsnapped.map((object): ValidationProblem => {
    const room = object.roomExternalRef ? roomByRef.get(object.roomExternalRef) : undefined;
    return {
      kind: "object-not-snapped-to-wall",
      severity: "warning",
      message:
        `${object.label}${room ? ` in ${room.name}` : ""} is not snapped to a ` +
        `wall. Please snap it to a wall so it is accounted for in the emitter ` +
        `checks — the length, sill and door clearances cannot be applied to a ` +
        `free-standing emitter. The heat loss itself is unaffected.`,
      roomId: room?.id ?? null,
      roomName: room?.name ?? null,
      selectId: object.roomExternalRef ?? null,
      surfaceId: null,
      objectId: object.id,
    };
  });
  return report([...base.problems, ...extra]);
}

/** Blocking first, then by room, so the list reads as a work queue. */
function report(problems: readonly ValidationProblem[]): ValidationReport {
  const order: Record<ValidationSeverity, number> = { blocking: 0, warning: 1 };
  const sorted = [...problems].sort(
    (a, b) =>
      order[a.severity] - order[b.severity] ||
      (a.roomName ?? "").localeCompare(b.roomName ?? "") ||
      a.kind.localeCompare(b.kind),
  );
  const blocking = sorted.filter((p) => p.severity === "blocking").length;
  return {
    problems: sorted,
    blocking,
    warnings: sorted.length - blocking,
    heatLossBlocked: blocking > 0,
  };
}

/**
 * The sentence the heat-loss section shows while a figure cannot be trusted.
 *
 * Plain, and specific about what it is waiting for. "There may be issues" is
 * not a warning, it is a disclaimer; a designer needs to know whether to act.
 */
export function heatLossTrustSentence(report: ValidationReport): string | null {
  if (!report.heatLossBlocked) return null;
  const kinds = new Set(report.problems.filter((p) => p.severity === "blocking").map((p) => p.kind));
  const parts: string[] = [];
  if (kinds.has("room-type-assumed")) parts.push("a room type nobody has chosen");
  if (kinds.has("room-temperature-below-minimum")) parts.push("a temperature below Table 1");
  if (kinds.has("room-no-walls")) parts.push("a room with no walls");
  if (kinds.has("surface-u-value-assumed")) parts.push("U-values that are still placeholders");
  if (kinds.has("surface-other-side-assumed")) parts.push("walls or floors whose other side nobody has confirmed");
  return (
    `This figure cannot be trusted yet: ${report.blocking} ` +
    `${report.blocking === 1 ? "problem" : "problems"} still to answer — ` +
    `${parts.join(", ")}. Work through them in Checks beside the plan.`
  );
}
